/**
 * The create-bulk-order wizard's `sessionStorage` side-car.
 *
 * Exists so a reload mid-wizard does not lose the in-progress selection and allocation. It is not
 * save-and-resume: it dies with the tab, which is why it needs no backend `Draft` state.
 *
 * The reasoning, the alternatives that were costed and declined, and the ownership rule (the view
 * writes this; the store stays out of `pinia-plugin-persistedstate`) are recorded in
 * `../boq-context-repo/features/bulk-orders/tickets/ticket-decision-log.md` — **DL-93, DL-94,
 * DL-118**. Read those before changing the shape.
 *
 * ⚠️ **Scope: values and step position only.** Figures are never stored; a restored grid is
 * re-priced against `POST /bulk-orders/preview` on entry, so every number on screen is fetched
 * fresh (PE-682, DL-93).
 *
 * 🔴 **Re-priced, not pruned, and the difference matters** (PR #2261 review). This said the
 * restored grid was *"re-validated and pruned of what the server no longer accepts"*. DL-118's
 * prune is not built: `loadBasis` merges cells and drops no binding, and
 * `orders.createBulkOrder.allocate.errors.pruned` has no consumer. So a binding to a BOQ line
 * since ordered against finds no cell, renders with no figures and **no variation flag**, and
 * `itemMarkDoneBlocker` — which checks emptiness, quantity and specification only — waves it
 * through to Review. The refusal lands at placement. Narrow (it needs a reload *and* the bill
 * moving underneath) but silent, and it is A.4's to close, not a safety net that exists today.
 */
import type { ResultingOrderKind } from '@/common/utils/bulk-orders.utils';
import type { AllocationGrid } from '@/stores/bulk-order-wizard.store';
import type { StoredVendorPayment } from '@/common/utils/bulk-order-payment.utils';

/**
 * Bumped whenever {@link BulkOrderWizardSnapshot} changes shape.
 *
 * **2 (batch A.3b).** The Allocate step made the grid three levels deep — item, then site, then
 * that site's bindings to its own BOQ lines — where v1 held one number per `(item, site)` and had
 * nowhere to put a second line or the binding itself. `doneItemIds` and `activeItemId` joined it,
 * so a reload mid-allocation comes back on the item the officer was working on rather than at the
 * front of the strip. A v1 snapshot fails the guard and is discarded, which is the graceful path:
 * an empty wizard is always a correct outcome here.
 *
 * 🔴 **3 (batch A.4c, PE-884).** The wizard became **five** steps, and `currentStep` /
 * `maxReachedStep` are persisted as **raw integers**. A v2 snapshot holding `currentStep: 3` means
 * *Review* under four steps and would mean *Payment and delivery* under five, and its
 * `maxReachedStep: 3` would mark Payment visited and Review not. There is no safe reading of a v2
 * integer, so the bump is the fix rather than a migration: **every in-flight wizard is discarded on
 * deploy**, which is this file's own contract and the intended outcome. `deliveryDate` joined the
 * shape in the same bump, because it now gates entry to Review.
 *
 * **4 (batch A.4c release 2, PE-882).** `payments` joined the shape, because it gates Review too.
 * Bumped rather than read leniently: a v3 snapshot with no payments would restore safely (the gate
 * lands it on step 4), but a guard that accepts a missing field is the drift this version exists to
 * stop. The cost is that in-flight wizards are discarded on deploy, and a v3 wizard parked on Review
 * could not have placed against the new contract anyway.
 *
 * **5 (batch P.1, PE-981).** `confirmedOrderKinds` joined the shape: Review can now be reached
 * without step 4 running again, including straight after a reload, and this is what tells it
 * whether the payment details were confirmed for the orders the grid now makes. Bumped for the
 * same reason as 4; the feature flag is off in production, so what is discarded is test sessions.
 */
const SNAPSHOT_VERSION = 5;

/** Namespaced by app and feature so it cannot collide with another flow's. */
export const BULK_ORDER_WIZARD_SESSION_KEY = 'boq.bulk-order-wizard.v1';

/**
 * Context: the preview basis, the authoritative response and `previewPending` are deliberately
 * absent, and that has not changed with the grid. A stored figure could be shown against a bill
 * that has since moved, and a pending flag restored as `true` is a request nobody made, spinning
 * forever. What is stored is **what the officer typed** — quantities, bindings, rate overrides,
 * done state — and the figures are fetched fresh on entry. That split is the whole design: the
 * work survives, the numbers are never stale.
 */
export interface BulkOrderWizardSnapshot {
  version: number;
  organisationId: string;
  currentStep: number;
  maxReachedStep: number;
  selectedProjectId: string | null;
  selectedProjectName: string | null;
  selectedItems: unknown[];
  selectedSiteIds: string[];
  /**
   * `itemId → siteId → bindings`. Carries the BOQ-line binding and any inline new-line draft, not
   * only the quantity — a restored grid that had lost which line a quantity was against would be
   * a grid the officer has to re-bind row by row.
   */
  allocationGrid: AllocationGrid;
  rateOverrides: Record<string, number>;
  doneItemIds: string[];
  activeItemId: string | null;
  /**
   * The placement's delivery date, as an ISO string because a `Date` does not survive JSON.
   *
   * Context: the one figure-adjacent value this side-car carries, and it is here because it
   * **gates** step 5 (PE-884). The exclusion note this file used to carry argued a restored session
   * should re-ask, which was cheap while the control sat on Review and is not once a gate stands in
   * front of it. The worry behind that note — a date chosen before midnight and restored into the
   * past, which the server refuses — is answered on read rather than by dropping the field: the
   * shell discards a restored date that is no longer far enough ahead.
   */
  deliveryDate: string | null;
  /**
   * How each resulting order is paid, keyed by vendor, dates as ISO strings.
   *
   * Context: what the officer typed on step 4, persisted for the reason `deliveryDate` is: it
   * gates step 5. Not a figure: the deposit is stored as its percentage (an amount typed converts
   * at full precision), and its money is worked out again against freshly fetched totals. Those
   * totals are the officer's own quantities at their own rates, so re-pricing an unchanged grid
   * lands on the same amount; a grid edit sends them back through step 4, where the new amount
   * shows before Review.
   */
  payments: Record<string, StoredVendorPayment>;
  /**
   * The kind of order (bulk or standard) each vendor's payment details were confirmed for on step
   * 4 (PE-981). A classification rather than a figure, so it is safe to restore.
   */
  confirmedOrderKinds: Record<string, ResultingOrderKind>;
}

const isArrayOf = (value: unknown, entryGuard: (entry: unknown) => boolean): boolean =>
  Array.isArray(value) && value.every(entryGuard);

const isRecordOf = (value: unknown, valueGuard: (entry: unknown) => boolean): boolean =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.values(value).every(valueGuard);

const isFiniteNumber = (value: unknown): boolean =>
  typeof value === 'number' && !Number.isNaN(value);

const isNullableString = (value: unknown): boolean => value === null || typeof value === 'string';

/**
 * A restored proposed line has to carry all four fields, not merely be an object.
 *
 * 🔴 **The guard stopped one level short** (PR #2261 review). Any object passed, and
 * `itemMarkDoneBlocker` reads `binding.proposedLine.specification.trim()` — so a restored `{}`
 * raised a `TypeError` on the one control standing between the officer and Review. That is exactly
 * what this guard's own contract argues against: a shape that half-restores is worse than one that
 * does not restore at all.
 */
const isUsableProposedLine = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;

  const draft = value as Record<string, unknown>;

  return (
    typeof draft.name === 'string' &&
    typeof draft.specification === 'string' &&
    typeof draft.sectionName === 'string' &&
    isNullableString(draft.subSectionName)
  );
};

/**
 * One stored binding, checked field by field for the same reason the whole guard exists: a shape
 * that half-restores is worse than one that does not restore at all, because the wizard then
 * carries a row pointing at nothing and fails at placement rather than here.
 */
const isUsableBinding = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;

  const b = value as Record<string, unknown>;

  return (
    typeof b.id === 'string' &&
    isNullableString(b.boqLineItemId) &&
    isNullableString(b.boqLineName) &&
    (b.proposedLine === null || isUsableProposedLine(b.proposedLine)) &&
    (b.quantity === null || isFiniteNumber(b.quantity))
  );
};

const isNullableNumber = (value: unknown): boolean => value === null || isFiniteNumber(value);

/** One stored payment, field by field, for the reason `isUsableBinding` gives. */
const isUsablePayment = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;

  const p = value as Record<string, unknown>;
  const deposit = p.deposit as Record<string, unknown> | null | undefined;

  return (
    isNullableString(p.method) &&
    isNullableString(p.dueDate) &&
    isNullableString(p.assigneeId) &&
    !!deposit &&
    typeof deposit === 'object' &&
    typeof deposit.isOn === 'boolean' &&
    isNullableNumber(deposit.percent) &&
    isNullableString(deposit.dueDate) &&
    isNullableString(deposit.assigneeId)
  );
};

/**
 * Whether a parsed snapshot is shaped as the wizard expects, field by field.
 *
 * Context: the version check alone is not enough, and the gap is a crash rather than a bad
 * restore. `{"version":1,"organisationId":"<current org>"}` passed the old guard, put `undefined`
 * into `selectedItems`, and the next read of the `selectedItemIds` computed threw on `.map` — a
 * blank wizard. The realistic route in is a future shape change that drops a field without
 * bumping `SNAPSHOT_VERSION`, which is exactly what the version was meant to catch: a guard whose
 * only enforcement is someone remembering.
 */
const isUsableSnapshot = (value: unknown): value is BulkOrderWizardSnapshot => {
  if (!value || typeof value !== 'object') return false;

  const s = value as Record<string, unknown>;

  return (
    s.version === SNAPSHOT_VERSION &&
    typeof s.organisationId === 'string' &&
    isFiniteNumber(s.currentStep) &&
    isFiniteNumber(s.maxReachedStep) &&
    isNullableString(s.selectedProjectId) &&
    isNullableString(s.selectedProjectName) &&
    Array.isArray(s.selectedItems) &&
    s.selectedItems.every((item) => !!item && typeof item === 'object') &&
    Array.isArray(s.selectedSiteIds) &&
    s.selectedSiteIds.every((id) => typeof id === 'string') &&
    isRecordOf(s.allocationGrid, (sites) =>
      isRecordOf(sites, (bindings) => isArrayOf(bindings, isUsableBinding)),
    ) &&
    isRecordOf(s.rateOverrides, isFiniteNumber) &&
    isArrayOf(s.doneItemIds, (id) => typeof id === 'string') &&
    isNullableString(s.activeItemId) &&
    isNullableString(s.deliveryDate) &&
    isRecordOf(s.payments, isUsablePayment) &&
    isRecordOf(s.confirmedOrderKinds, (kind) => kind === 'bulkOrder' || kind === 'standardOrder')
  );
};

/**
 * Reads the snapshot for `organisationId`, or `null` when there is nothing usable.
 *
 * Three rejections and a catch-all, all returning `null` because an empty wizard is always a
 * correct outcome here: a snapshot whose shape does not match this build, one from another
 * organisation (`$reset` clears the key on a switch, but a snapshot can outlive a switch that took
 * another path), and any storage or parse failure — `sessionStorage` itself throws in a browser
 * set to block site data.
 */
export const readBulkOrderWizardSession = (
  organisationId: string,
): BulkOrderWizardSnapshot | null => {
  try {
    const raw = globalThis.sessionStorage?.getItem(BULK_ORDER_WIZARD_SESSION_KEY);
    if (!raw) return null;

    const snapshot: unknown = JSON.parse(raw);

    if (!isUsableSnapshot(snapshot)) return null;
    if (snapshot.organisationId !== organisationId) return null;

    return snapshot;
  } catch {
    return null;
  }
};

/** Writes the snapshot. Gives up silently if storage refuses — the wizard works without it. */
export const writeBulkOrderWizardSession = (
  snapshot: Omit<BulkOrderWizardSnapshot, 'version'>,
): void => {
  try {
    globalThis.sessionStorage?.setItem(
      BULK_ORDER_WIZARD_SESSION_KEY,
      JSON.stringify({ ...snapshot, version: SNAPSHOT_VERSION }),
    );
  } catch {
    // Storage unavailable or full.
  }
};

/** Removes the snapshot. Called by the store's `$reset` and on a confirmed exit. */
export const clearBulkOrderWizardSession = (): void => {
  try {
    globalThis.sessionStorage?.removeItem(BULK_ORDER_WIZARD_SESSION_KEY);
  } catch {
    // Nothing to do — see `readBulkOrderWizardSession`.
  }
};
